import { format, isValid, parseISO } from 'date-fns'

const REPORT_SHARED_TYPES = new Set([
    'shared_report',
    'shared_report_send',
])

const TICKET_TYPES = new Set([
    'autoticketnotify',
    'ticket assigned',
    'autoticketdigest',
    'tickets digest',
    'autoticketevent',
    'ticketupdatenotification',
    'ticket update',
    'autoticketreminder',
    'ticket reminder',
    'autoticketescalated',
    'ticket escalated',
    'autoticketoverdue',
    'ticket overdue',
    'sendcreateraiseemailnotification',
    'ticket raised',
    'ticketsignaturerequest',
    'ticket signature request',
    'ticketclosednotification',
    'ticket closed',
])

const WORKFLOW_TYPES = new Set(['section_workflow_notification'])

export const EMAIL_HISTORY_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'report', label: 'Report shared' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'workflow', label: 'Workflow' },
    { id: 'failed', label: 'Failed only' },
]

export const EMAIL_HISTORY_PAGE_SIZE = 50

const pick = (obj, keys) => {
    if (!obj || typeof obj !== 'object') return undefined
    for (let i = 0; i < keys.length; i += 1) {
        const value = obj[keys[i]]
        if (value !== undefined && value !== null) return value
    }
    return undefined
}

const isExplicitFalse = value =>
    value === false ||
    value === 0 ||
    value === 'false' ||
    value === 'False' ||
    value === '0'

const unwrapEmailHistory = raw => {
    if (!raw || typeof raw !== 'object') return {}
    const data = raw.Data || raw.data
    if (
        data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        (data.Items ||
            data.items ||
            data.TotalCount != null ||
            data.totalCount != null ||
            data.HistoryAvailable != null ||
            data.historyAvailable != null)
    ) {
        return data
    }
    return raw
}

export function emailTypeGroup(type) {
    const key = String(type || '')
        .trim()
        .toLowerCase()
    if (REPORT_SHARED_TYPES.has(key)) return 'report'
    if (TICKET_TYPES.has(key)) return 'tickets'
    if (WORKFLOW_TYPES.has(key)) return 'workflow'
    return 'other'
}

export function emailMatchesFilter(item, filterId) {
    if (!item) return false
    if (filterId === 'failed') return item.sent === false
    if (filterId === 'report' || filterId === 'tickets' || filterId === 'workflow') {
        return emailTypeGroup(item.type) === filterId
    }
    return true
}

export function splitAddresses(value) {
    if (value == null || value === '') return []
    return String(value)
        .split(/[;,]/)
        .map(part => part.trim())
        .filter(Boolean)
}

export function formatEmailSentOn(sentOnUtc) {
    if (!sentOnUtc) return ''
    let raw = String(sentOnUtc).trim()
    if (!raw) return ''
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)
    if (!hasZone) raw = `${raw}Z`
    const date = parseISO(raw)
    if (!isValid(date)) return ''
    return format(date, "MMM d, yyyy '·' h:mm a")
}

export function normalizeAuditEmailPage(raw, requestedPage = 1) {
    const root = unwrapEmailHistory(raw)
    const itemsRaw = root?.Items || root?.items || []
    const source = Array.isArray(itemsRaw) ? itemsRaw : []
    const items = source.map((item, index) => {
        const sentOnUtc = pick(item, ['SentOnUtc', 'sentOnUtc']) || ''
        const emailLogId = pick(item, ['EmailLogId', 'emailLogId'])
        const type = String(pick(item, ['Type', 'type']) || '')
        const typeLabel = String(
            pick(item, ['TypeLabel', 'typeLabel']) || type || 'Email',
        )
        return {
            emailLogId: String(
                emailLogId || `email-${requestedPage}-${index}-${sentOnUtc}`,
            ),
            sentOnUtc: sentOnUtc ? String(sentOnUtc) : '',
            type,
            typeLabel,
            subject: String(pick(item, ['Subject', 'subject']) ?? ''),
            to: String(pick(item, ['To', 'to']) || ''),
            cc: String(pick(item, ['Cc', 'cc']) || ''),
            sent: !isExplicitFalse(pick(item, ['Sent', 'sent'])),
            error: String(pick(item, ['Error', 'error']) || ''),
        }
    })

    const availableFlag = root?.HistoryAvailable ?? root?.historyAvailable
    const historyAvailable = !isExplicitFalse(availableFlag)
    const parsedCount = Number(root?.TotalCount ?? root?.totalCount)
    return {
        historyAvailable,
        totalCount: Number.isFinite(parsedCount) ? parsedCount : items.length,
        page: Number(root?.Page ?? root?.page) || requestedPage,
        items,
    }
}

export function emailHistoryErrorKind(err) {
    const status = Number(err?.status)
    const body =
        err?.error && typeof err.error === 'object' && !Array.isArray(err.error)
            ? err.error
            : {}
    const code = String(body.code || body.Code || body.errorCode || '')
    if (status === 401) return 'unauthorized'
    if (status === 404) return 'not-found'
    if (status === 400 || code === 'INVALID_PAGING') return 'paging'
    return 'retry'
}

export function getReportRowAuditId(item) {
    if (!item) return ''
    const id =
        item.AuditUniqueId ||
        item.auditUniqueId ||
        item.AuditTemplateId ||
        item.auditTemplateId ||
        item.AuditId ||
        item.auditId
    if (id == null || id === '') return ''
    return id
}

export function dedupeEmailHistory(items) {
    const seen = new Set()
    return (items || []).filter(item => {
        const key = item?.emailLogId
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}
